import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { useSensors } from '../../hooks/useSensors';
import { useSensorNames } from '../../context/SensorNamesContext';
import { useSensorHistory } from '../../hooks/useSensorHistory';
import { batteryPercentFromVolts, BATTERY_FULL_VOLTS } from '../../utils/battery';

type Range = 'day' | 'week' | 'month';
/** `solar: null` = esa lectura no trae voltaje de panel. NO es cero. */
type HistoryPoint = { ts: string; battery: number; solar: number | null; volts: number };

const RANGE_LABELS: Record<Range, string> = { day: '24h', week: '7d', month: '30d' };
const RANGE_HOURS: Record<Range, number> = { day: 24, week: 24 * 7, month: 24 * 30 };

export default function BatteryPage() {
  const { t } = useTranslation();
  const { sensors, loading } = useSensors();
  const { getName } = useSensorNames();
  const [selectedImei, setSelectedImei] = useState<string>('');
  const [range, setRange] = useState<Range>('day');

  const activeImei = selectedImei || sensors[0]?.imei || '';
  const selectedSensor = sensors.find(s => s.imei === activeImei);
  const criticalSensors = sensors.filter(s => (s.battery ?? 100) < 20);

  /**
   * Histórico REAL, desde la base.
   *
   * Antes salía de getDemoBatteryHistory(), que buscaba el imei en una lista
   * de sensores demo y —al no encontrarlo, que es SIEMPRE el caso de un
   * sensor real— caía a `sensor?.battery ?? 50` y dibujaba una curva
   * alrededor del 50% con Math.random(). Por eso la tarjeta decía 100% y la
   * gráfica de abajo mostraba 50: eran dos fuentes distintas.
   */
  const { readings, loading: histLoading, error: histError } =
    useSensorHistory(activeImei || null, { hours: RANGE_HOURS[range] });

  const history: HistoryPoint[] = useMemo(() => {
    /**
     * PRIMERO se juntan las filas del mismo instante.
     *
     * El decoder de Xiaoma emite un reading por cada bloque de la trama, así
     * que battery_voltage y solar_panel_voltage llegan en FILAS DISTINTAS de
     * telemetry con la misma marca de tiempo. La versión anterior recorría
     * fila por fila y descartaba toda la que no trajera batería — o sea,
     * todas las del panel solar. Por eso la ventana de 24 h (que lee la
     * tabla cruda) no dibujaba nada de solar, mientras que 7 y 30 días sí:
     * ese camino pasa por el agregado horario, que ya junta las métricas de
     * un mismo bucket en un solo payload.
     */
    const porInstante = new Map<string, { battery?: number; solar?: number }>();
    for (const r of readings) {
      if (!r.receivedAt) continue;
      const acc = porInstante.get(r.receivedAt) ?? {};
      const b = r.payload?.battery_voltage;
      const sv = r.payload?.solar_panel_voltage;
      if (typeof b === 'number' && Number.isFinite(b)) acc.battery = b;
      if (typeof sv === 'number' && Number.isFinite(sv)) acc.solar = sv;
      porInstante.set(r.receivedAt, acc);
    }

    // El voltaje del panel se grafica como % de su propio pico en la ventana
    // porque el eje se comparte con el porcentaje de batería y el pico real
    // depende del modelo de panel. La escala se calcula en la gráfica, no
    // aquí, para poder descartar valores atípicos con el resto de la serie
    // a la vista.
    const out: HistoryPoint[] = [];
    for (const [ts, { battery, solar }] of porInstante) {
      const pct = batteryPercentFromVolts(battery);
      if (pct == null) continue;   // sin batería no hay punto que ubicar
      out.push({
        ts,
        battery: pct,
        // El voltaje crudo se conserva junto al porcentaje: arriba de
        // BATTERY_FULL_VOLTS la curva satura en 100 % y la línea se aplana,
        // así que el volt es lo único que distingue "batería llena que sigue
        // variando" de "el equipo repite siempre el mismo número".
        volts: battery!,
        // Sin voltaje de panel va null, no 0: un cero se dibuja como "el
        // panel no generó nada" y eso es una afirmación sobre algo que
        // nadie midió. Con null, la línea simplemente no pasa por ahí.
        solar: solar ?? null,
      });
    }
    out.sort((a, b) => a.ts.localeCompare(b.ts));
    return out;
  }, [readings]);

  /** Voltaje real medido en la ventana — ver el comentario del bloque que lo pinta. */
  const rangoVolts = useMemo(() => {
    if (history.length === 0) return null;
    const vs = history.map(h => h.volts);
    return { min: Math.min(...vs), max: Math.max(...vs) };
  }, [history]);

  /**
   * Toda la ventana por encima del 100 % de la curva del fabricante.
   *
   * Cuando pasa, el porcentaje deja de servir: la línea es una recta y la
   * descarga de la noche —que sí ocurre, sólo que entre 4.28 y 4.29 V— queda
   * escondida contra el techo de la escala. En ese caso la gráfica cambia a
   * volts. NO se toca la curva de YaTun para "estirar" el porcentaje: esa
   * curva es su especificación y moverla sería inventar una lectura que el
   * equipo no dio. Se cambia la escala, nunca el dato.
   */
  const modoVolts = rangoVolts != null && rangoVolts.min >= BATTERY_FULL_VOLTS;

  if (loading) {
    return (
      <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <span style={{ fontSize: 13, color: 'var(--gray)' }}>Loading sensors…</span>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>{t('battery.title')}</h1>
          <p style={{ fontSize: 13, color: 'var(--gray)' }}>
            {sensors.length} sensors &nbsp;·&nbsp;
            {criticalSensors.length > 0
              ? <span style={{ color: 'var(--red)', fontWeight: 600 }}>{criticalSensors.length} critical</span>
              : <span style={{ color: 'var(--green)' }}>{t('battery.allHealthy')}</span>}
          </p>
        </div>
      </div>

      {/* Critical banner */}
      {criticalSensors.length > 0 && (
        <div style={{
          background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10,
          padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#991b1b',
          display: 'flex', gap: 8, alignItems: 'center',
        }}>
          <span>⚠️</span>
          <span>
            <strong>{criticalSensors.map(s => getName(s.imei, s.name)).join(', ')}</strong>
            {' '}— battery critically low. Check solar panel connection or replace battery.
          </span>
        </div>
      )}

      {/* Sensor cards grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 16,
        marginBottom: 28,
      }}>
        {sensors.map(s => {
          // null y NO 0 cuando el equipo no reporta batería: con `?? 0` la
          // tarjeta mostraba un 0% ROJO —una alarma de batería crítica
          // inventada— para un sensor del que simplemente no sabemos la carga.
          const battery = s.battery ?? null;
          // El backend deriva el % del voltaje con la curva oficial del
          // fabricante (spec V1.4 §6.4, ver batteryPercentFromVolts). El
          // voltaje se conserva porque es con lo que se diagnostica la celda;
          // el porcentaje es para leer de un vistazo.
          const batV = s.batteryVoltage;
          const solarV = s.solarPanelVoltage;
          // El color vuelve a salir del porcentaje también con voltaje: desde
          // que el fabricante publicó la curva (spec V1.4 §6.4) el backend
          // deriva un % real del voltaje, así que ya no hay razón para pintar
          // todo verde sólo porque el dato venga en volts.
          const batColor = battery == null
            ? 'var(--gray)'
            : battery > 40 ? 'var(--green)' : battery > 20 ? 'var(--amber)' : 'var(--red)';
          const isSelected = s.imei === activeImei;

          return (
            <div
              key={s.id}
              onClick={() => setSelectedImei(s.imei)}
              style={{
                background: 'var(--white)',
                border: `1.5px solid ${isSelected ? 'var(--green)' : 'var(--border)'}`,
                borderRadius: 14,
                padding: '16px',
                cursor: 'pointer',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                boxShadow: isSelected ? '0 0 0 3px rgba(34,197,94,0.12)' : 'none',
              }}
            >
              {/* Name + status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: batColor, flexShrink: 0, boxShadow: `0 0 5px ${batColor}` }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', lineHeight: 1.2 }}>{getName(s.imei, s.name)}</div>
              </div>

              {/* El porcentaje manda: es lo que la gente lee de un vistazo. El
                  voltaje medido va debajo, que es el dato con el que se
                  diagnostica de verdad si algo anda mal con la celda. */}
              <div style={{ fontSize: 34, fontWeight: 700, color: batColor, lineHeight: 1, marginBottom: 2 }}>
                {battery != null ? `${battery}%` : '—'}
              </div>
              <div style={{ fontSize: 10, color: 'var(--gray)', marginBottom: 10 }}>
                {batV != null ? `Battery charge · ${batV.toFixed(2)} V measured` : 'Battery charge'}
              </div>

              {/* Sin porcentaje no hay barra: un largo inventado se lee como
                  una medición. */}
              <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden', marginBottom: 14 }}>
                {battery != null && (
                  <div style={{ width: `${battery}%`, height: '100%', background: batColor, borderRadius: 3, transition: 'width 0.4s' }} />
                )}
              </div>

              {/* Panel solar. Sin dato va "—" y NO "0 W": `solarCharge` es un
                  campo de los datos demo que el backend nunca emite, así que
                  ese 0 W era un cero inventado — indistinguible de un panel
                  que de verdad no está generando. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <span style={{ fontSize: 14 }}>☀️</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: solarV != null && solarV > 1 ? '#d97706' : 'var(--gray)' }}>
                  {solarV != null ? `${solarV.toFixed(2)} V` : '—'}
                </span>
                <span style={{ fontSize: 10, color: 'var(--gray)' }}>solar panel</span>
              </div>

              {/* Miniatura: la serie real de las últimas 24 h del sensor
                  seleccionado. Para el resto no se dibuja nada en vez de
                  inventar una curva — antes todas mostraban la misma. */}
              {isSelected && history.length >= 2
                ? <MiniChart data={history.map(h => h.battery)} color={batColor} min={0} max={100} />
                : null}
            </div>
          );
        })}
      </div>

      {/* Detail chart */}
      {selectedSensor && (
        <div style={{
          background: 'var(--white)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: '22px 28px',
        }}>
          {/* Chart header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{getName(selectedSensor.imei, selectedSensor.name)}</div>
              <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>{t('battery.history')}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['day', 'week', 'month'] as Range[]).map(r => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  style={{
                    padding: '5px 14px', borderRadius: 6, border: '1px solid',
                    borderColor: range === r ? 'var(--green)' : 'var(--border)',
                    background: range === r ? 'var(--green-bg)' : 'transparent',
                    color: range === r ? 'var(--green-dk)' : 'var(--gray)',
                    fontSize: 12, cursor: 'pointer',
                    fontWeight: range === r ? 600 : 400,
                    transition: 'all 0.15s',
                  }}
                >
                  {RANGE_LABELS[r]}
                </button>
              ))}
            </div>
          </div>

          {/* Rango de voltaje medido en la ventana.
              Responde a la pregunta obvia cuando la línea sale plana en
              100 %: el porcentaje viene de la curva del fabricante, que ya
              marca 100 % desde 4.20 V, así que una batería que se mantiene
              arriba de eso dibuja una recta aunque el voltaje sí se mueva.
              Sin el volt a la vista, eso es indistinguible de un dato
              congelado — y son dos cosas muy distintas. */}
          {rangoVolts && (
            <div style={{ fontSize: 11, color: 'var(--gray)', marginBottom: 10 }}>
              Measured battery {rangoVolts.min.toFixed(2)}–{rangoVolts.max.toFixed(2)} V
              {modoVolts && (
                <> · full charge for the whole window ({BATTERY_FULL_VOLTS.toFixed(2)} V and up all
                   read as 100%), so the chart switches to volts — that is the only way to see the
                   night discharge</>
              )}
            </div>
          )}

          {/* Legend */}
          <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="22" height="4"><line x1="0" y1="2" x2="22" y2="2" stroke="var(--green)" strokeWidth="2.5" /></svg>
              <span style={{ fontSize: 11, color: 'var(--gray)' }}>{modoVolts ? 'Battery (V)' : 'Battery %'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="22" height="4"><line x1="0" y1="2" x2="22" y2="2" stroke="#f59e0b" strokeWidth="2" strokeDasharray="4,2" /></svg>
              <span style={{ fontSize: 11, color: 'var(--gray)' }}>Solar output (% of peak)</span>
            </div>
          </div>

          {/* Gráfica con datos reales. Sin historia no se dibuja nada: una
              curva inventada se lee como la tendencia del sensor. */}
          {histLoading ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--gray)' }}>
              Loading history…
            </div>
          ) : history.length >= 2 ? (
            <HistoryChart data={history} modoVolts={modoVolts} />
          ) : (
            <div style={{ height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--gray)' }}>
                {histError
                  ? "Couldn't load the history"
                  : 'Not enough battery history for this sensor yet'}
              </span>
              <span style={{ fontSize: 11, color: 'var(--gray)' }}>
                {histError ? 'Try again in a moment' : `Range: last ${RANGE_LABELS[range]}`}
              </span>
            </div>
          )}

          {/* Stats row */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16,
            marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border)',
          }}>
            {(() => {
              // Mismo criterio que las tarjetas: sin dato de batería no se
              // inventa un 0% ni una autonomía "Critical".
              const bat = selectedSensor.battery ?? null;
              // `solarCharge` es un campo de los datos demo que el backend NUNCA
              // emite: este StatBox mostraba "0 W" siempre, contradiciendo el
              // voltaje real que la tarjeta de arriba sí muestra. Se usa el
              // voltaje medido del panel, que es lo que el equipo reporta.
              const solarV = selectedSensor.solarPanelVoltage;
              const batColor = bat == null
                ? 'var(--gray)'
                : bat > 40 ? 'var(--green)' : bat > 20 ? 'var(--amber)' : 'var(--red)';
              const autonomy = bat == null ? '—' : bat > 20 ? `~${Math.round(bat / 0.3)} hrs` : 'Critical';
              return (
                <>
                  <StatBox label={t('battery.current')} value={bat != null ? `${bat}%` : '—'} color={batColor} />
                  <StatBox
                    label={t('battery.solarOutput')}
                    value={solarV != null ? `${solarV.toFixed(2)} V` : '—'}
                    color={solarV != null ? '#d97706' : 'var(--gray)'}
                  />
                  <StatBox label={t('battery.autonomy')} value={autonomy} color={bat != null && bat > 20 ? 'var(--dark)' : 'var(--gray)'} />
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────

/**
 * `min`/`max` fijan la escala. Sin ellos se usa el rango de los propios
 * datos, y entonces una serie constante —una batería clavada en 100 %— se
 * dibuja pegada al PISO, que se lee justo al revés: batería vacía. Con la
 * escala 0–100 explícita, llena se ve arriba.
 */
function MiniChart({ data, color, min: minProp, max: maxProp }: {
  data: number[]; color: string; min?: number; max?: number;
}) {
  if (data.length < 2) return null;
  const W = 200, H = 36, pad = 2;
  const min = minProp ?? Math.min(...data);
  const max = maxProp ?? Math.max(...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * W;
      const y = H - pad - ((v - min) / range) * (H - 2 * pad);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const area = `0,${H} ${pts} ${W},${H}`;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      {/* fill-opacity y no `${color}18`: el color llega como var(--green) y
          concatenarle la transparencia daba un color inválido, que el SVG
          resolvía como NEGRO — de ahí la mancha oscura en la miniatura. */}
      <polygon fill={color} fillOpacity={0.1} points={area} />
      <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" points={pts} />
    </svg>
  );
}

/**
 * Hueco a partir del cual la línea se corta en vez de unir dos puntos.
 *
 * Se saca del propio muestreo: si las lecturas vienen cada hora, un salto de
 * más de tres horas es una interrupción real y no se debe dibujar como si el
 * sensor hubiera seguido reportando. Se usa la mediana y no el promedio,
 * porque un solo hueco largo arrastraría el promedio y ya nada se cortaría.
 */
function pasoTipico(data: HistoryPoint[]): number {
  const saltos: number[] = [];
  for (let i = 1; i < data.length; i++) {
    const dt = Date.parse(data[i]!.ts) - Date.parse(data[i - 1]!.ts);
    if (dt > 0) saltos.push(dt);
  }
  if (saltos.length === 0) return Infinity;
  saltos.sort((a, b) => a - b);
  return saltos[Math.floor(saltos.length / 2)]!;
}

/** Puntos con valor, partidos en tramos continuos. */
type Tramo = Array<{ ts: string; valor: number }>;

/**
 * Parte una serie en tramos dibujables.
 *
 * Corta en dos casos, que son el mismo problema visto de dos maneras: cuando
 * la métrica no viene en esa lectura, y cuando entre dos lecturas pasó
 * demasiado tiempo. En los dos, unir los extremos con una recta inventa
 * información que nadie midió.
 */
function tramosDe(
  data: HistoryPoint[],
  valor: (d: HistoryPoint) => number | null,
  corte: number,
): Tramo[] {
  const tramos: Tramo[] = [];
  let actual: Tramo = [];
  let anterior: number | null = null;

  for (const d of data) {
    const v = valor(d);
    const t = Date.parse(d.ts);
    const rompe = v == null || (anterior !== null && t - anterior > corte);
    if (rompe && actual.length > 0) {
      tramos.push(actual);
      actual = [];
    }
    if (v != null) {
      actual.push({ ts: d.ts, valor: v });
      anterior = t;
    } else {
      anterior = null;
    }
  }
  if (actual.length > 0) tramos.push(actual);
  // Un tramo de un solo punto no dibuja línea.
  return tramos.filter(t => t.length >= 2);
}

function HistoryChart({ data, modoVolts }: { data: HistoryPoint[]; modoVolts: boolean }) {
  if (data.length < 2) return null;
  const W = 800, H = 170;
  const pad = { top: 12, right: 16, bottom: 28, left: 36 };
  const iw = W - pad.left - pad.right;
  const ih = H - pad.top - pad.bottom;

  /**
   * Escala del panel solar resistente a valores atípicos.
   *
   * Antes se normalizaba contra el MÁXIMO de la ventana. Una sola lectura
   * anómala alta aplastaba el resto de la serie contra el piso y la línea se
   * veía plana aunque los datos estuvieran bien — por eso se notaba en la
   * vista de 30 días y no en la de 7: mientras más larga la ventana, más
   * probable el valor raro. Con el percentil 95 un punto atípico ya no decide
   * la escala de los otros setecientos; lo que se pase se recorta arriba en
   * lugar de arrastrar todo hacia abajo.
   */
  const solares = data
    .map(d => d.solar)
    .filter((v): v is number => v != null && v > 0)
    .sort((a, b) => a - b);
  const maxSolar = solares.length > 0
    ? Math.max(solares[Math.floor(solares.length * 0.95)] ?? 1, 1)
    : 1;

  // Eje X por TIEMPO REAL, no por posición en el arreglo. Con posiciones, una
  // caída de tres días se cruzaba como si no hubiera pasado nada y las
  // etiquetas de fecha de abajo quedaban corridas respecto a los puntos.
  const t0 = Date.parse(data[0]!.ts);
  const t1 = Date.parse(data[data.length - 1]!.ts);
  const span = Math.max(1, t1 - t0);
  const xDe = (ts: string) => pad.left + ((Date.parse(ts) - t0) / span) * iw;
  const yEn = (val: number, lo: number, hi: number) =>
    pad.top + (1 - (Math.min(Math.max(val, lo), hi) - lo) / (hi - lo)) * ih;

  /**
   * Escala de la batería: 0–100 % normalmente, y en volts cuando el
   * porcentaje está saturado (ver `modoVolts` arriba).
   *
   * El span mínimo es a propósito. Con sólo el mínimo y el máximo medidos, un
   * rizo de 0.005 V se estiraría a toda la altura de la gráfica y se leería
   * como una descarga severa. Con un piso de 0.08 V, una variación chica se
   * ve chica y una grande se ve grande — y las etiquetas del eje van en volts
   * reales, así que la magnitud siempre se puede leer.
   */
  const SPAN_MINIMO_V = 0.08;
  const voltajes = data.map(d => d.volts);
  const centroV = (Math.min(...voltajes) + Math.max(...voltajes)) / 2;
  const mitadV = Math.max(Math.max(...voltajes) - Math.min(...voltajes), SPAN_MINIMO_V) / 2;
  const escalaBat = modoVolts
    ? { lo: centroV - mitadV, hi: centroV + mitadV }
    : { lo: 0, hi: 100 };

  const corte = pasoTipico(data) * 3;
  const tramosBateria = tramosDe(data, d => (modoVolts ? d.volts : d.battery), corte);
  const tramosSolar = tramosDe(data, d => d.solar, corte);

  const serie = (t: Tramo, lo: number, hi: number) =>
    t.map(p => `${xDe(p.ts).toFixed(1)},${yEn(p.valor, lo, hi).toFixed(1)}`).join(' ');
  // El área se cierra sobre la base del PROPIO tramo, no sobre el ancho total,
  // para que el relleno tampoco cruce los huecos.
  const area = (t: Tramo, pts: string) =>
    `${xDe(t[0]!.ts).toFixed(1)},${pad.top + ih} ${pts} ${xDe(t[t.length - 1]!.ts).toFixed(1)},${pad.top + ih}`;

  // Seis marcas repartidas sobre el TIEMPO, no sobre el arreglo. En ventanas
  // largas la hora estorba más de lo que informa, así que arriba de tres días
  // se muestra sólo la fecha.
  const soloFecha = span > 3 * 24 * 3600_000;
  const timeLabels = Array.from({ length: 6 }, (_, k) => {
    const t = new Date(t0 + (k / 5) * span);
    const fecha = `${t.getMonth() + 1}/${t.getDate()}`;
    return {
      x: pad.left + (k / 5) * iw,
      label: soloFecha ? fecha : `${fecha} ${String(t.getHours()).padStart(2, '0')}h`,
    };
  });

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
      {/* Rejilla. Las etiquetas siguen a la escala de la batería: si la
          gráfica está en volts, el eje dice volts. */}
      {[0, 0.25, 0.5, 0.75, 1].map(f => {
        const y = pad.top + (1 - f) * ih;
        const valor = escalaBat.lo + f * (escalaBat.hi - escalaBat.lo);
        return (
          <g key={f}>
            <line x1={pad.left} x2={pad.left + iw} y1={y} y2={y} stroke="var(--border)" strokeWidth="0.8" />
            <text x={pad.left - 5} y={y + 4} textAnchor="end" fontSize="9" fill="var(--gray)">
              {modoVolts ? `${valor.toFixed(2)}V` : `${Math.round(valor)}%`}
            </text>
          </g>
        );
      })}

      {/* Solar area + line */}
      {tramosSolar.map((t, i) => {
        const pts = serie(t, 0, maxSolar);
        return (
          <g key={`sol-${i}`}>
            <polygon fill="rgba(245,158,11,0.08)" points={area(t, pts)} />
            <polyline
              fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="5,3"
              strokeLinejoin="round" points={pts}
            />
          </g>
        );
      })}

      {/* Battery area + line */}
      {tramosBateria.map((t, i) => {
        const pts = serie(t, escalaBat.lo, escalaBat.hi);
        return (
          <g key={`bat-${i}`}>
            <polygon fill="rgba(34,197,94,0.07)" points={area(t, pts)} />
            <polyline
              fill="none" stroke="var(--green)" strokeWidth="2.2"
              strokeLinejoin="round" points={pts}
            />
          </g>
        );
      })}

      {/* Time labels */}
      {timeLabels.map(({ x, label }) => (
        <text key={x} x={x} y={H - 5} textAnchor="middle" fontSize="8.5" fill="var(--gray)">{label}</text>
      ))}
    </svg>
  );
}

function StatBox({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '14px 18px' }}>
      <div style={{ fontSize: 10, color: 'var(--gray)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
